<template>
    <div class="slider-input__container">
        <vue-slider
            class="slider-input"
            :class="(moved ? `slider-input-${colorVariant}` : `slider-input-${colorVariant}--unmoved`) + ' ' + (isCenterAndEven ? 'slider-input--process-hidden' : '')"
            v-model="value"
            @change="handleChange"
            @drag-start="initDrag"
            :clickable="!center"
            dot-size="45"
            :dot-options="dotOptions"
            height="8px"
            :min="range[0]"
            :max="range[1]"
            :tooltip="tooltip"
            :order="false"
            :interval="1"
            :marks="stepped"
            :hide-label="hideStepLabels"
        >
            <template v-slot:tooltip="{ value }" v-if="showMarkTooltip">
                <div class="slider-input__mark-tooltip">
                    <div class="slider-input__mark-value">
                        {{ value }}
                    </div>
                    <div
                        class="slider-input__mark-tooltip-bar mx-auto"
                        :style="{ height: markTooltipHeight }"
                    >
                    </div>
                </div>
            </template>
            <template v-slot:dot>
                <div
                    class="slider-input__dot"
                >
                    <div class="slider-input__dot-content h-100 d-flex align-items-center justify-content-center">
                        <img
                            v-svg-inline
                            width="15"
                            height="15"
                            class="slider-input__arrow"
                            src="@/assets/ui/slider-arrow-left.svg"
                            alt="example"
                        />
                        <img
                            v-svg-inline
                            width="15"
                            height="15"
                            class="slider-input__arrow"
                            src="@/assets/ui/slider-arrow-right.svg"
                            alt="example"
                        />
                    </div>
                </div>
            </template>
        </vue-slider>
        <div v-if="midMarker" class="slider-input__mid-marker" />
        <div class="slider-input__labels d-flex justify-content-between" v-if="showLabels">
            <div class="slider-input__label d-flex flex-column justify-content-between" :class="showRange ? 'slider-input__label--range' : ''">
                <div v-show="showRange" class="slider-input__range-label mb-2">
                    {{ range[0] }}
                </div>
                <div :class="showRange ? 'slider-input__range-label-description' : 'slider-input__range-label-description mt-3'">
                    {{ labelMin }}
                </div>
            </div>

            <div class="slider-input__label text-center d-flex justify-content-center" :class="showRange ? 'slider-input__label--range align-items-end' : 'align-items-start'">
                {{ labelMid }}
            </div>

            <div class="slider-input__label text-right d-flex flex-column justify-content-between" :class="showRange ? 'slider-input__label--range' : ''">
                <div v-show="showRange" class="slider-input__range-label mb-2">
                    {{ range[1] }}
                </div>
                <div :class="showRange ? 'slider-input__range-label-description' : 'slider-input__range-label-description mt-3'">
                    {{ labelMax }}
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import VueSlider from 'vue-slider-component'
import '@/styles/components/vue-slider-component.scss'
import { ASSETS_URL } from '@/constants/misc'

import colorVariantMixin from '@/components/mixins/colorVariantMixin'
export default {
    name: 'slider',
    components: {
        VueSlider,
    },
    mixins: [colorVariantMixin],
    props: {
        range: {
            type: Array,
            default: () => [0, 100]
        },
        start: {
            // start="center" gives us slider which can move from the middle in both directions -
            // this implies that the value which is bound to the v-model will be an array
            // because we have to use two sliderhandles (one hidden) to accomplish the right behavior
            // the array is handled respectively when emitting an answer (only the value of slider which
            // is actually moved is emitted etc.)
            type: String
        },
        stepped: {
            type: Boolean,
            default: false
        },
        showTooltip: {
            type: Boolean,
            default: false
        },
        showMarkTooltip: {
            type: Boolean,
            default: false,
        },
        markTooltipHeight: {
            type: String
        },
        showLabels: {
            type: Boolean,
            default: true
        },
        hideStepLabels: {
            type: Boolean,
            default: true
        },
        labelMin: {
            type: String,
        },
        labelMid: {
            type: String,
            default: ''
        },
        labelMax: {
            type: String,
        },
        showRange: {
            type: Boolean,
            default: true
        },
        midMarker: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            ASSETS_URL,
            value: this.range[0],
            moved: false,
        }
    },
    computed: {
        center() {
            return this.start === 'center'
        },
        dotOptions() {
            return this.center ? [{
                disabled: true,
                min: 0,
                max: 0,
            }, {
                min: this.range[0],
                max: this.range[1],
            }] : []
        },
        tooltip() {
            if(this.showMarkTooltip) {
                return 'always'
            }
            if(this.showTooltip) {
                return 'active'
            } else {
                return 'none'
            }
        },
        isCenterAndEven() {
            return this.center && this.stepped && this.range[1]%2 === 0
        }
    },
    methods: {
        handleChange(value) {
            if(!this.moved) this.moved = true
            if(Array.isArray(value)) {
                this.$emit('change', value[value.length - 1])
            } else {
                this.$emit('change', value)
            }
        },
        initDrag() {
            if(this.isCenterAndEven) {
                // ATTENTION: if we have a centered slider with a range that has no actual center,
                // we don't allow setting a value/answering by initDrag method i.e. just "clicking" on the circle
                // because the circle won't be at a discrete value
            } else {
                if(!this.moved) {
                    this.moved = true
                    if(Array.isArray(this.value)) {
                        this.$emit('change', Math.round(this.value[this.value.length - 1]))
                    } else {
                        this.$emit('change', Math.round(this.value))
                    }
                }
            }

        }
    },
    mounted() {
        if(this.center) {
            const rangeCenter = (this.range[0] + this.range[1])/2
            this.value = [rangeCenter, rangeCenter]

            // NOTE: hack to hide the dot for the first (pseudo-)slider in center mode manually
            // as there apparently is no way of either hiding the first dot through vue-slider config
            // or using a CSS selector to do it when using a custom slot for the element
            const dots = this.$el.getElementsByClassName('slider-input__dot')
            if(dots.length > 0) dots[0].classList.add('d-none')

            const tooltips = this.$el.getElementsByClassName('slider-input__mark-tooltip')
            if(tooltips.length > 0) tooltips[0].classList.add('d-none')
        } else {
            this.value = this.range[0] || 0
        }
    }
}
</script>
<style lang="scss">
.slider-input__container {
    position: relative;
}
.slider-input__dot {
    height: 45px;
    width: 45px;
    background-color: $bq-silver;
    border-radius: 50%;
    border: 1px solid white;
    z-index: 1000;
}

.slider-input__dot-content {
    color: white;
    text-align: center;
}

.slider-input__mark-value {
    font-size: 1.775em;
    transform: translateY(12px)
}

.slider-input__arrow {
    outline: 0;
}

.slider-input__labels {
    margin-top: -1.125rem;
} 

.slider-input__label {
    width: 90px;
    min-height: 35px;
    font-size: 0.725rem;
    line-height: 1.15;
    @media screen and (min-width: 768px) {
        font-size: 0.925rem;
        width: 120px;
        line-height: 1.25;
    }
}

.slider-input__label--range {
    min-height: 48px;
}

.slider-input__range-label-description {
    min-height: 38px;
}

.slider-input__range-label {
    font-size: 0.75rem;
    font-weight: 600;
}

.slider-input__mark-tooltip-bar {
    top: 0;
    width: 5px;
    border-radius: 5px 5px 0 0;
    transform: translateY(11px);
    z-index: -2;
}

.slider-input__mid-marker {
    position: absolute;
    top: 14px;
    left: calc(50% - 1px);
    width: 2px;
    height: 26px;
    margin-bottom: -36px;
    background-color: #ccc;
}


.slider-input {
    &-bq-burgundy {
        .slider-input__dot {
            background-color: $bq-burgundy;
        }

        .vue-slider-process {
            background-color: $bq-burgundy;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-burgundy;
        }
    }

    &-bq-burgundy--unmoved {
        .vue-slider-process {
            background-color: $bq-burgundy;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-burgundy;
        }
    }

    &-bq-green {
        .slider-input__dot {
            background-color: $bq-green;
        }

        .vue-slider-process {
            background-color: $bq-green;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-green;
        }
    }

    &-bq-green--unmoved {
        .vue-slider-process {
            background-color: $bq-green;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-green;
        }
    }

    &-bq-blue {
        .slider-input__dot {
            background-color: $bq-blue;
        }

        .vue-slider-process {
            background-color: $bq-blue;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-blue;
        }
    }

    &-bq-blue--unmoved {
        .vue-slider-process {
            background-color: $bq-blue;
        }


        .slider-input__mark-tooltip-bar {
            background-color: $bq-blue;
        }
    }

    &-bq-turquoise {
        .slider-input__dot {
            background-color: $bq-turquoise;
        }

        .vue-slider-process {
            background-color: $bq-turquoise;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-turquoise;
        }
    }

    &-bq-turquoise--unmoved {
        .vue-slider-process {
            background-color: $bq-turquoise;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-turquoise;
        }
    }

    &-bq-orange {
        .slider-input__dot {
            background-color: $bq-orange;
        }

        .vue-slider-process {
            background-color: $bq-orange;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-orange;
        }
    }

    &-bq-orange--unmoved {
        .vue-slider-process {
            background-color: $bq-orange;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-orange;
        }
    }

    &-bq-red {
        .slider-input__dot {
            background-color: $bq-red;
        }

        .vue-slider-process {
            background-color: $bq-red;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-red;
        }
    }

    &-bq-red--unmoved {
        .vue-slider-process {
            background-color: $bq-red;
        }

        .slider-input__mark-tooltip-bar {
            background-color: $bq-red;
        }
    }
}

.slider-input--process-hidden {
    .vue-slider-process {
        background-color: #ccc !important;
    }
}
</style>
